import { ReactNode, useState } from 'react';

export interface RoadmapArtwork {
  /** Catalog title; falls back to "Untitled object" placeholder when empty. */
  title?: string;
  /** ALI catalog ID (e.g. "DA-55L.1150"). Hidden when undefined/empty. */
  objectId?: string;
  /** Primary image thumbnail URL. When falsy, a no-image icon is rendered. */
  thumbUrl?: string | null;
  /** Optional alt text for the thumbnail (defaults to title). */
  thumbAlt?: string;
}

export interface RoadmapSaveStatus {
  state: 'saved' | 'saving' | 'error';
  label: ReactNode;
}

interface RoadmapProps {
  collapsed?: boolean;
  onToggleCollapse?: () => void;
  /** When provided, renders an artwork header block above the step list. */
  artwork?: RoadmapArtwork;
  /** When provided, renders a save-status footer below the step list. */
  saveStatus?: RoadmapSaveStatus;
  children: ReactNode;
}

const NoImageIcon = ({ size = 22 }: { size?: number }) => (
  <svg
    viewBox="0 0 24 24"
    width={size}
    height={size}
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    aria-hidden
  >
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <circle cx="8.5" cy="9" r="1.5" />
    <path d="M21 15l-5-5L5 21" />
  </svg>
);

interface RoadmapArtworkBlockProps {
  artwork: RoadmapArtwork;
  collapsed: boolean;
}

const RoadmapArtworkBlock = ({ artwork, collapsed }: RoadmapArtworkBlockProps) => {
  const title = artwork.title?.trim() || '';
  const isUntitled = title === '';
  const displayTitle = isUntitled ? 'Untitled object' : title;
  const tooltip = artwork.objectId ? `${displayTitle} · ${artwork.objectId}` : displayTitle;
  const iconSize = collapsed ? 18 : 22;

  const thumbnail = artwork.thumbUrl ? (
    <img src={artwork.thumbUrl} alt={artwork.thumbAlt ?? displayTitle} />
  ) : (
    <NoImageIcon size={iconSize} />
  );

  const rootClass = collapsed ? 'rm-artwork rm-artwork--collapsed' : 'rm-artwork';
  const metadata = !collapsed && (
    <div className="rm-artwork__meta">
      <div
        className="rm-artwork__title"
        data-untitled={isUntitled ? 'true' : 'false'}
        data-testid="edit-object-roadmap-artwork-title"
      >
        {displayTitle}
      </div>
      {artwork.objectId ? (
        <div
          className="rm-artwork__id"
          data-testid="edit-object-roadmap-artwork-id"
        >
          {artwork.objectId}
        </div>
      ) : null}
    </div>
  );

  return (
    <div
      className={rootClass}
      data-testid="edit-object-roadmap-artwork"
      title={tooltip}
      aria-label={tooltip}
    >
      <div className="rm-artwork__thumb" data-empty={artwork.thumbUrl ? 'false' : 'true'}>
        {thumbnail}
      </div>
      {metadata}
    </div>
  );
};

/**
 * Roadmap sidebar matching the design's `.roadmap`. Header has an eyebrow
 * label "Your Progress" and a collapse toggle. Body is a vertical list of
 * `<RmStep>` plus an optional collapsible group of optional steps.
 *
 * When an `artwork` prop is supplied, an identifier block (thumbnail + title
 * + ALI ID pill) renders below the head. In collapsed mode, only a mini
 * thumbnail is shown with a hover tooltip carrying the title and ID.
 */
export const Roadmap = ({
  collapsed,
  onToggleCollapse,
  artwork,
  saveStatus,
  children,
}: RoadmapProps) => (
  <aside className="roadmap">
    <div className="rm-head">
      <span className="eyebrow">Your Progress</span>
      {onToggleCollapse && (
        <button
          type="button"
          className="rm-toggle"
          onClick={onToggleCollapse}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          aria-pressed={collapsed ? 'true' : 'false'}
        >
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </button>
      )}
    </div>
    {artwork ? <RoadmapArtworkBlock artwork={artwork} collapsed={!!collapsed} /> : null}
    {children}
    {saveStatus && !collapsed ? (
      <div className="rm-foot" data-testid="edit-object-roadmap-save-status">
        <span className={`save-status ${saveStatus.state}`} data-state={saveStatus.state}>
          <span className="dot" />
          {saveStatus.label}
        </span>
      </div>
    ) : null}
  </aside>
);

interface RmStepProps {
  /** 1-based step number; rendered inside the dot. */
  number: number;
  label: ReactNode;
  /** When true, the row gets the active red background. */
  active?: boolean;
  /** Optional steps render with a dashed-border dot and slate text. */
  optional?: boolean;
  /** Right-aligned tag (e.g. "OPT"). */
  tag?: ReactNode;
  onClick?: () => void;
}

export const RmStep = ({ number, label, active, optional, tag, onClick }: RmStepProps) => {
  const classNames = ['rm-step', active && 'active', optional && 'optional']
    .filter(Boolean)
    .join(' ');
  return (
    <button type="button" className={classNames} onClick={onClick}>
      <span className="dot">{number}</span>
      <span style={{ flex: 1, textAlign: 'left' }}>{label}</span>
      {tag && <span className="opt-tag">{tag}</span>}
    </button>
  );
};

interface RmOptGroupProps {
  /** Heading label (e.g. "Optional steps"). */
  label: ReactNode;
  /** Number-added counter for the right-aligned pill. */
  count?: ReactNode;
  /** Whether to render expanded by default. */
  defaultExpanded?: boolean;
  children: ReactNode;
}

export const RmOptGroup = ({ label, count, defaultExpanded, children }: RmOptGroupProps) => {
  const [expanded, setExpanded] = useState(defaultExpanded ?? false);
  return (
    <>
      <button
        type="button"
        className="rm-opt-toggle"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
      >
        <span className="opt-icon">
          <svg viewBox="0 0 24 24" width="10" height="10" fill="currentColor">
            <path d={expanded ? 'M19 13H5v-2h14z' : 'M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6z'} />
          </svg>
        </span>
        <span className="opt-label">{label}</span>
        {count !== undefined && <span className="opt-count">{count}</span>}
      </button>
      <div className="rm-opt-group" data-expanded={expanded ? '1' : '0'}>
        {children}
      </div>
    </>
  );
};
